import { Candidato, ResultadoCandidato } from "../../Candidato/Candidato";
import MembroBanca from "../MembroBanca";
import VotoUtils, { Voto } from "./Voto";

export const VotacaoRefreshTimer = 10000 //10s

export enum StatusVotacao {
    NAO_INICIADA = "Não Iniciada",
    ABERTA = "Aberta",
    FECHADA = "Fechada",
}

export enum ResultadoFinal {
    VALIDADO = "Validado",
    NAO_VALIDADO = "Não Validado",
    AUSENTE = "Ausente",
    NAO_DEFINIDO = "Não definido"
}

export interface Votacao {
  id: string;
  candidato: Candidato;
  votos: Voto[];
  quantidadeVotos?: number;
  dataInicio?: Date;
  dataFim?: Date;
  status: StatusVotacao;
  resultado?: ResultadoFinal | null;
}


class VotacaoUtils {
  /**
   * Calcula o resultado final de uma votação para um candidato específico.
   * @param votacao - Objeto da votação.
   * @returns Resultado final do candidato.
   */
  static calcularResultadoFinal(votacao: Votacao): ResultadoFinal | null {
    const contagemVotos = VotoUtils.contarVotosPorResultado(votacao.votos);
    if (contagemVotos[ResultadoCandidato.VALIDADO] > contagemVotos[ResultadoCandidato.NAO_VALIDADO]) {
      return ResultadoFinal.VALIDADO;
    }
    if (contagemVotos[ResultadoCandidato.NAO_VALIDADO] > contagemVotos[ResultadoCandidato.VALIDADO]) {
      return ResultadoFinal.NAO_VALIDADO;
    }
    return null;
  }

  /**
   * Adiciona um voto a uma votação específica.
   * @param votacao - Objeto da votação.
   * @param voto - Novo voto a ser adicionado.
   * @returns Objeto de votação atualizado.
   */
  static adicionarVoto(votacao: Votacao, voto: Voto): Votacao {
    if (!VotoUtils.validarVoto(voto)) {
      throw new Error('Justificativa é obrigatória para votos NÃO_VALIDADO.');
    }
    votacao.votos = VotoUtils.adicionarVoto(votacao.votos, voto);
    votacao.resultado = VotacaoUtils.calcularResultadoFinal(votacao);
    return votacao;
  }

  /**
   * Verifica se um votante já votou em uma votação específica.
   * @param votacao - Objeto da votação.
   * @param votante - Objeto da Pessoa votante.
   * @returns Verdadeiro se o votante já votou, falso caso contrário.
   */
  static votanteJaVotou(votacao: Votacao, votante: MembroBanca): boolean {
    return VotoUtils.votanteJaVotou(votacao.votos, votante);
  }

  /**
   * Retorna todos os votos de uma votação específica.
   * @param votacao - Objeto da votação.
   * @returns Lista de votos.
   */
  static obterVotos(votacao: Votacao): Voto[] {
    return votacao.votos;
  }

  /**
   * Audita os votos de uma votação específica.
   * @param votacao - Objeto da votação.
   * @returns Informações de auditoria dos votos.
   */
  static auditarVotos(votacao: Votacao): string {
    return votacao.votos.map(voto =>
      `Votante: ${voto.votante?.pessoa.nome}, Resultado: ${voto.resultado}, Justificativa: ${voto.justificativa || 'N/A'}, Data: ${voto.data}`
    ).join('\n');
  }

  /**
   * Atualiza o status de uma votação.
   * @param votacao - Objeto da votação.
   * @param status - Novo status da votação.
   * @returns Objeto de votação atualizado.
   */
  static atualizarStatus(votacao: Votacao, status: StatusVotacao): Votacao {
    votacao.status = status;
    return votacao;
  }

  static marcarVotanteComoAusente(votacao: Votacao): Votacao {    
    votacao.resultado = ResultadoFinal.AUSENTE;
    return votacao;
  }

}

export default VotacaoUtils;
